import { useEffect, useRef, useState } from 'react';
import { ACTIVE_AI_TEMPLATES, AI_TEMPLATE_NAMES } from '../constants/constants';
import { AI_STUDIO_SOURCE, buildAIStudioUrl } from '../helpers/aiStudioHelper';
import { errorToast } from '../helpers/toastHelper';
import Loader from './Loader';
import { MagicToolIcon } from '../assets/icons';
import './EditWithAIMenu.scss';
import { getModelPermissions, IMAGINE_AI_PERMISSIONS } from '../api/imagineai/permissions';
import { userPilotEvents } from './Userpilot/userpilotHelper';

// Inner dropdown content used by both renderers
function TemplateList({ loadingTemplate, onPick }) {
  return (
    <div className="edit-with-ai-menu__dropdown">
      <div className="edit-with-ai-menu__title">Edit with AI</div>
      {ACTIVE_AI_TEMPLATES.map((templateId) => (
        <button
          key={templateId}
          type="button"
          className="edit-with-ai-menu__item"
          disabled={loadingTemplate && loadingTemplate !== templateId}
          onClick={(e) => {
            e.stopPropagation();
            onPick(templateId);
          }}>
          <span>{AI_TEMPLATE_NAMES[templateId]}</span>
          {loadingTemplate === templateId ? (
            <Loader loading size={12} thickness={1} position="relative" />
          ) : null}
        </button>
      ))}
    </div>
  );
}

function useAIPermission() {
  const checkAndToast = async () => {
    try {
      const res = await getModelPermissions();
      const permissions = res?.permissions || [];
      if (!permissions.includes(IMAGINE_AI_PERMISSIONS.IMAGINE_AI_V2)) {
        errorToast('You do not have permission to use this feature. Please contact your admin.');
        return false;
      }
      return true;
    } catch (err) {
      errorToast('Could not verify permissions. Please try again.');
      return false;
    }
  };

  return { checkAndToast };
}

// Hook holding the shared menu state + URL handling
function useEditWithAIMenu({ getImageUrl }) {
  const [open, setOpen] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(null);
  const ref = useRef(null);
  const { checkAndToast } = useAIPermission();
  const { trackAIStudioEvent } = userPilotEvents();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePick = async (templateId, data) => {
    if (loadingTemplate) return;
    try {
      setLoadingTemplate(templateId);
      const imageUrl = await getImageUrl?.();
      if (!imageUrl) {
        errorToast('Image is not ready yet. Please try again.');
        return;
      }
      trackAIStudioEvent(`AI - Clicked - ${AI_TEMPLATE_NAMES[templateId]}`, {
      click_from:"Edit with AI",
      file_name: `${data?.data?.name}_${data?.data?.images[data?.activeIndex || 0]?.angle_name}`,
      render_Id: data?.data?.id,
      image_url: data?.slides?.[data?.activeIndex || 0],
      tools_type:AI_TEMPLATE_NAMES[templateId]
      })
      window.open(
        buildAIStudioUrl(`discover/${templateId}`, { image_url: imageUrl }, { source: AI_STUDIO_SOURCE.EDIT_WITH_AI }),
        '_blank'
      );
      setOpen(false);
    } catch (err) {
      console.error('Edit with AI: failed to redirect', err);
      errorToast('Unable to open AI Studio. Please try again.');
    } finally {
      setLoadingTemplate(null);
    }
  };

  return { open, setOpen, loadingTemplate, handlePick, ref, checkAndToast };
}

// Default export: standalone button trigger (for ImagineAI / generic placements)
export default function EditWithAIMenu({
  getImageUrl,
  disabled,
  triggerLabel = 'Edit with AI',
  triggerClassName = '',
  triggerIcon: TriggerIcon = MagicToolIcon,
  placement = 'top',
}) {
  const { open, setOpen, loadingTemplate, handlePick, ref, checkAndToast } = useEditWithAIMenu({ getImageUrl });

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (disabled) return;
    if (open) {
      setOpen(false);
      return;
    }
    const allowed = await checkAndToast();
    if (!allowed) return;
    setOpen(true);
  };

  return (
    <div
      ref={ref}
      className={`edit-with-ai-menu edit-with-ai-menu--${placement}`}
      onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`edit-with-ai-menu__big-trigger ${triggerClassName} ${
          disabled ? 'is-disabled' : ''
        }`}
        onClick={toggle}
        disabled={disabled}>
        {TriggerIcon ? <TriggerIcon /> : null}
        <span>{triggerLabel}</span>
      </button>
      {open && <TemplateList loadingTemplate={loadingTemplate} onPick={handlePick} />}
    </div>
  );
}

// FooterButton-shaped variant: renders as <li> for use inside RenderDetails footer-action-list
export function EditWithAIFooterButton({
  footerButton = true,
  isDisabled,
  isDisabledCallback,
  getImageUrl,
  title = 'Edit with AI',
  icon: Icon = MagicToolIcon,
  id,
  data = null
}) {
  const { open, setOpen, loadingTemplate, handlePick, ref, checkAndToast } = useEditWithAIMenu({ getImageUrl });
  const { trackClickEvent } = userPilotEvents();
  if (!footerButton) return null;

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (isDisabled) {
      isDisabledCallback?.();
      return;
    }
    if (open) {
      setOpen(false);
      return;
    }
    trackClickEvent('AI - Edit With AI', {
      file_name: `${data?.data?.name}_${data?.data?.images[data?.activeIndex || 0]?.angle_name}`,
      render_Id: data?.data?.id,
      image_url: data?.slides?.[data?.activeIndex || 0],
    });
    const allowed = await checkAndToast();
    if (!allowed) return;
    setOpen(true);
  };

  return (
    <li id={id} title={open ? "" : title} className="edit-with-ai-menu edit-with-ai-menu--top" ref={ref}>
      <div
        role="button"
        onClick={toggle}
        className={`text-light d-flex flex-column justify-content-center align-items-center ${
          isDisabled ? 'opacity-50' : ''
        }`}>
        {Icon ? <Icon /> : null}
        <div className="text-center text-nowrap" style={{ fontSize: '0.75rem' }}>
          {title}
        </div>
      </div>
      {open && <TemplateList loadingTemplate={loadingTemplate} onPick={(id)=>handlePick(id,data)} />}
    </li>
  );
}
